import { CreateBallotRequest, CreateBallotResponse, CreateElectionRequest, CreateElectionResponse, CreatePointRequest, CreatePointResponse, ElectionStanding, GetBallotResponse, GetElectionResponse } from '../../types';
import { buildQueryString, del, get, logWithAmplitude, post, put, throwHttpError } from './api';
import { getFromLocalStorage, getIpAddress, isDefined, undefinedIfEmpty } from '../utils';

import accountInfo from '../state/account-info';
import { getMainPage } from '../page-name';
import topicData from '../state/topic-data';

export async function getElection(amplitude_callsite: string, topic_id: string, election_id: string, topic_key?: string) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the election.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Election', {
        election_id,
    });
    return await (response.json() as Promise<GetElectionResponse>);
}

export async function getElections(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });

    const response = await fetch(`/api/topics/${topic_id}/elections${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the elections.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get elections', {
        topic_id
    });

    const elections = await (response.json() as Promise<GetElectionResponse[]>);


    // Lots of annoying issue because there was once a bug where lots of elections were created for the same topic.
    // One day we can remove this code, but for now we need to ensure that we only return unique elections.
    // First sort all elections by most recently updated
    elections.sort((a, b) => {
        const aDate = a.updated_at;
        const bDate = b.updated_at;
        return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
    });

    // Helper function to check if an election is currently active in any way
    const isElectionActive = (election: GetElectionResponse): boolean => {
        return election.active || // Voting is live
               election.nomination_is_live || // Nominations are live
               (isDefined(election.show_leaders) && election.show_leaders > 0); // Results are public
    };

    // Split elections into different groups with these priorities:
    // 1. Active elections (voting, nominations, or results visible)
    // 2. Non-"My Election" named elections
    // 3. "My Election" default named elections
    const activeElections = elections.filter(isElectionActive);
    const inactiveNonDefaultElections = elections.filter(e =>
        !isElectionActive(e) && e.name !== 'My Election');
    const inactiveDefaultElections = elections.filter(e =>
        !isElectionActive(e) && e.name === 'My Election');

    // Combine all groups in priority order
    return [
        ...activeElections,
        ...inactiveNonDefaultElections,
        ...inactiveDefaultElections
    ];
}

export async function updateElection(
    amplitude_callsite: string,
    topic_id: string,
    election_id: string,
    election: CreateElectionRequest,
    topic_key?: string,
    additional_logged_parameters: Object = {}
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}${queryString}`, put(election));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the election.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Election Updated',
        Object.assign(
            {},
            {
                election_id,
                topic_key,
            },
            additional_logged_parameters
        )
    );
}

export async function deleteElection(
    amplitude_callsite: string,
    topic_id: string,
    election_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}${queryString}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the election.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Election Deleted', {
        topic_id,
        election_id,
        topic_key,
    });
}

export async function addElection(
    amplitude_callsite: string,
    topic_id: string,
    election: CreateElectionRequest,
    topic_key?: string,
) {
    const election_response = await addElectionInner(topic_id, election, topic_key);
    logWithAmplitude(amplitude_callsite, 'Election Added', {
        topic_id,
        topic_key,
        election_id: election_response._id.$oid,
    });
    return election_response;
}

async function addElectionInner(
    topic_id: string,
    election: CreateElectionRequest,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections${queryString}`, post(election));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the election.', response);
    }

    return await (response.json() as Promise<CreateElectionResponse>);
}

export async function getBallot(amplitude_callsite: string, topic_id: string, election_id: string, ballot_id: string, topic_key?: string) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}/ballot/${election_id}${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the ballots.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Ballot', {
        election_id,
    });
    return await (response.json() as Promise<GetBallotResponse>);
}

export async function getBallotsForElection(
    amplitude_callsite: string,
    topic_id: string,
    election_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });

    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}/ballots${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the ballots for election.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Ballot', {
        topic_id
    });

    return await (response.json() as Promise<GetBallotResponse[]>);
}

export async function getBallotsForAccount(
    amplitude_callsite: string,
    election_id: string,
    email: string,
) {
    const response = await fetch(`/api/accounts/${email}/elections/${election_id}/ballots`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the ballots.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Ballots', {
        email
    });

    return await (response.json() as Promise<GetBallotResponse[]>);
}

export async function deleteBallot(
    amplitude_callsite: string,
    ballot_id: string,
    topic_id: string,
) {
    let qs = '';
    if (topic_id) {
        qs = buildQueryString({ topic_id });
    }
    const response = await fetch(`/api/topics/${topic_id}/ballots/${ballot_id}${qs}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the ballot.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Ballot Deleted', {
        topic_id,
    });
}

export async function addBallot(
    amplitude_callsite: string,
    election_id: string,
    ballot: CreateBallotRequest,
) {
    const ballot_response = await addBallotInner(election_id, ballot);
    logWithAmplitude(amplitude_callsite, 'Ballot Added', {
        ballot: ballot_response._id.$oid,
    });
    return ballot_response;
}

async function addBallotInner(
    election_id: string,
    ballot: CreateBallotRequest,
) {
    const response = await fetch(`/api/elections/${election_id}/ballots`, post(ballot));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the ballot.', response);
    }

    return await (response.json() as Promise<CreateBallotResponse>);
}

export async function checkBallotEligibility(
    topic_id: string,
    ip_address: string,
    election_id: string,
    email: string,
    topic_key?: string,
) {
    const qs = buildQueryString({ email, ip_address, topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}/eligibility${qs}`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while checking the ballot eligibility.', response);
    }

    return await (response.json() as Promise<boolean>);
}

// get election leaders
export async function getElectionLeaders(election_id: string, topic_id: string, topic_key?: string) : Promise<ElectionStanding[]> {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/elections/${election_id}/leaders${queryString}`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting the election leaders.', response);
    }

    return await (response.json() as Promise<ElectionStanding[]>);
}

// helpers

export async function isVoterEligible(election?: GetElectionResponse, user_email?:string): Promise<boolean> {
    if (!election) {
        return false;
    }
    const email = user_email ?? undefinedIfEmpty(accountInfo.email) ?? getFromLocalStorage('email');
    const ip_address = await getIpAddress();

    if (isDefined(email) && topicData.topic && isDefined(election)) {
        try {
            const eligible = await checkBallotEligibility(topicData.topicId, ip_address, election._id.$oid, email, topicData.topicKey);
            topicData.ballotEligible = eligible;
            return eligible;
        } catch (error) {
            return true;
        }
    }
    // You should also return a default value in case the if condition is not met
    return true;
}

export function electionVotingIsHappening (election?: GetElectionResponse): boolean {
    if (!election) {
        return false;
    }
    return (election.active);
}

export function electionNominationsAreHappening (election?: GetElectionResponse): boolean {
    if (!election) {
        return false;
    }
    return (election.nomination_is_live);
}

export function electionResultsAreLive (election?: GetElectionResponse): boolean {
    if (!election) {
        return false;
    }
    return isDefined(election.show_leaders) && election.show_leaders > 0;
}

export function createElectionRequestFromGetElectionResponse(response: GetElectionResponse): CreateElectionRequest {
    return {
        name: response.name,
        active: response.active,
        description: response.description,
        category_descriptions: response.category_descriptions,
        email_terms: response.email_terms,
        email_opt_in_required: response.email_opt_in_required,
        verify_email: response.verify_email,

        show_leaders: response.show_leaders,
        nomination_is_live: response.nomination_is_live,

        start_time: response.start_time,
        end_time: response.end_time,

        point_button_text: response.point_button_text,
        submit_button_text: response.submit_button_text,
        multi_select: response.multi_select,
        frequency_quantity: response.frequency_quantity,
        frequency_recurrance: response.frequency_recurrance,
        check_ip_address: response.check_ip_address,

        excluded_categories: response.excluded_categories,
        excluded_points: response.excluded_points,
    };
}


export async function addNomination(
    amplitude_callsite: string,
    election_id: string,
    point: CreatePointRequest,
    topic_key?: string,
) {
    const point_response = await addNominationInner(election_id, point, topic_key);
    logWithAmplitude(amplitude_callsite, 'Nomination Added', {
        election_id,
        topic_key,
        point_id: point_response._id.$oid,
    });
    return point_response;
}

async function addNominationInner(
    election_id: string,
    point: CreatePointRequest,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/elections/${election_id}/nomination${queryString}`, post(point));
    if (!response.ok) {
        getMainPage().errorToastElement.showToast('An error occurred while creating the nomination, they may not be allowed at this time');
        await throwHttpError('An error occurred while creating the nomination.', response);
    }

    return await (response.json() as Promise<CreatePointResponse>);
}
